/**
 * Types and formatting shared by the alerting page's sections.
 *
 * The severity palette is the project's, not Tailwind's: Extreme, Severe,
 * Moderate and Minor each have one colour and it is the same colour on the
 * strip, on the map, in the table and on the timeline.
 */

import { API_BASE } from '../../api/client'
import { C } from '../../design/tokens'

export const SEVERITY_COLOUR: Record<string, string> = {
  Extreme: C.sevExtreme,
  Severe: C.sevSevere,
  Moderate: C.sevModerate,
  Minor: C.sevMinor,
}
export const SEVERITY_ORDER = ['Extreme', 'Severe', 'Moderate', 'Minor']

export const TIER_LABEL: Record<number, string> = {
  1: 'Tier 1 · immediate',
  2: 'Tier 2 · within 30 min',
  3: 'Tier 3 · within 2 h',
}

export interface Channel {
  label: string
  reach: string
  latency: string
  latency_min: number
  acknowledged: boolean
  ack_note: string
}

export interface Meta {
  channels: Record<string, Channel>
  tiers: Record<string, string>
  runs: { id: string; scenario: string | null }[]
  site: { id: string; display_name: string; india_region: string }
}

export interface SettlementAlert {
  alert: boolean
  severity: string | null
  urgency: string | null
  certainty: string | null
  lead_time_min: number | null
  trace: string[]
}

export interface SettlementRow {
  settlement_id: number
  name: string
  type: string | null
  district: string | null
  taluka: string | null
  lat: number
  lon: number
  population: number | null
  max_depth_m: number
  max_velocity_ms: number
  arrival_time_min: number | null
  hazard_class: number | null
  chainage_km: number | null
  alert: SettlementAlert
}

export interface DispatchEvent {
  t_dispatch_min: number
  t_notified_min: number
  timestamp: string
  exercise_clock: string
  recipient: string
  tier: number
  channel: string
  channel_key: string
  acknowledged: boolean
  severity: string
  settlement: string
  settlement_id: number
  arrival_time_min: number | null
  lead_time_min: number | null
}

export interface AckRow {
  recipient: string
  tier: number
  t_notified_min: number
  confirmation_due_min: number
  confirmation_path: boolean
  path_note: string
  escalates_to: string
  severity: string
  channels: string[]
  confirming_channels: string[]
  settlements: string[]
}

export interface LifecycleStep {
  msg_type: string
  t_min: number | null
  timestamp: string | null
  carries: string
  references: string | null
  state: string
}

export interface RuleRow {
  key: string
  rule: string
  value: number | Record<string, string>
  unit?: string
  min?: number
  max?: number
  step?: number
  source: string
  editable: boolean
}

export interface Evaluation {
  /** 'crossing' where the site has no populated place in its modelled extent. */
  location_kind?: 'crossing'
  no_populated_place?: boolean
  populated_places?: { inside: string[]; osm_places_checked: number; source: string }
  unclassified_locations?: (SettlementRow & { reason: string })[]
  districts?: string[]
  district_source?: string
  reach_hazard_km2?: Record<string, number>
  reach_excluded_km2?: number
  run: {
    run_id: string
    scenario: string | null
    duration_min: number
    still_spreading_at_end: boolean
    time_origin: string
  }
  exercise_start: string
  rules: RuleRow[]
  settlements: SettlementRow[]
  events: DispatchEvent[]
  population_reached: { t_min: number; cumulative_population: number; settlement: string }[]
  acknowledgement: AckRow[]
  escalation_routes: {
    tier: number
    tier_label: string
    escalates_to: string
    after_min: number
    recipients?: string[]
  }[]
  lifecycle: LifecycleStep[]
  directory: {
    id?: number
    reason?: string | null
    name: string
    role: string
    tier: number
    channels: string[]
    scope: string
    active: boolean
    settlement_ids: number[] | null
  }[]
}

export interface PipelineStage {
  id: string
  label: string
  sublabel: string
  kind: string
  edge_protocol: string | null
  edge_latency: string | null
}
export interface RouteHop {
  id: string
  label: string
  sublabel: string
  protocol: string
}
export interface Route {
  id: string
  label: string
  sublabel: string
  protocol: string
  latency: string
  channel_label: string
  hops: RouteHop[]
}
export interface Architecture {
  pipeline: PipelineStage[]
  routes: Route[]
  returns: { source: string; protocol: string }[]
  legend: { kind: string; label: string }[]
}

export interface LocalisedText {
  language: string
  text: string
  characters: number
  limit?: number
  within_limit?: boolean
  channel?: string
}
export interface IvrScript {
  language: string
  steps: { cue: string; text: string }[]
  spoken_seconds: number
  confirmation: string
}
export interface Siren {
  severity: string
  tone: string
  on_seconds: number
  off_seconds: number
  repeats: number
  cycle_seconds: number
  total_seconds: number
  meaning: string
  all_clear: string
  sounded_at: string
  timeline: { index: number; start_s: number; on_s: number; off_s: number }[]
}
export interface MessageSet {
  settlement: string
  settlement_id: number
  severity: string
  generated_from: {
    run_id: string
    scenario: string | null
    arrival_min: number | null
    depth_m: number | null
    sent: string
    onset: string | null
    expires: string
  }
  languages: { code: string; label: string }[]
  sms: LocalisedText[]
  cell_broadcast: LocalisedText[]
  crawl: LocalisedText[]
  ivr: IvrScript[]
  siren: Siren
  email: { subject: string; body: string; recipients: string }
  cap: { xml: string; card: Record<string, unknown> }
}

/** `1755` → `29 h 15 min`. */
export function hm(minutes: number): string {
  const whole = Math.round(minutes)
  return `${Math.floor(whole / 60)} h ${String(Math.abs(whole % 60)).padStart(2, '0')} min`
}

/** `T+29:15`, the exercise clock. */
export function clock(minutes: number): string {
  const whole = Math.round(minutes)
  return `T+${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(Math.abs(whole % 60)).padStart(2, '0')}`
}

export function istTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export function plusMinutes(iso: string, minutes: number): string {
  return istTime(new Date(new Date(iso).getTime() + minutes * 60_000).toISOString())
}

export async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const payload = (await response.json()) as { detail?: string }
      if (typeof payload.detail === 'string') detail = payload.detail
    } catch {
      /* the body was not JSON; the status line is what there is */
    }
    throw new Error(detail)
  }
  return (await response.json()) as T
}

/** Download text the browser has, without a round trip. */
export function download(filename: string, mime: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}
